import React, { useMemo, useState } from "react";
import { Box, Typography, Button, Grid2 } from "@mui/material";
import GenericDraggableTableView from "../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { PrimaryFormLoadingActionButton } from "../../../Features/Table/GenericDraggableTable/TableStyles";
import ErrorModal from "../Components/ErrorModal";

const Step3DataPreview = ({ selectedFile, selectedResourceType, previewData, onValidate, onBackToTable }) => {
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isValidating, setIsValidating] = useState(false);

    // Extract headers and data from previewData
    const headers = useMemo(() => previewData?.headers || [], [previewData]);
    const rows = useMemo(() => previewData?.previewData || [], [previewData]);

    // Transform headers into table columns
    const tableColumns = useMemo(() => {
        const formattedHeaders = {};
        headers.forEach((header, index) => {
            // const key = `previewHeader-${index}`;
            const noSpacesKey = header.replace(/\s+/g, "");
            formattedHeaders[noSpacesKey] = {
                key: noSpacesKey,
                title: header,
                width: 'auto',
                sorter: false,
                dataIndex: noSpacesKey,
                defaultValue: "-",
                valueRenderingType: "text",
                ...(index === 0 && {fixed: "left"}),
            }
        });
        const columnsConfig = getColumnsConfig({
            tableColumns: formattedHeaders,
            tableColumnOrder: Object.values(formattedHeaders).map(header => header.dataIndex),
            customRenderers: {},
        });
        return columnsConfig;
    }, [headers]);

    // Transform rows to add unique keys
    const tableData = useMemo(() => {
        return rows.map((row, index) => {
            const formattedRow = {};
            Object.keys(row).map(key => {
                const noSpacesKey = key.replace(/\s+/g, "");
                formattedRow[noSpacesKey] = row[key];
            })
            return formattedRow;
        });
    }, [rows]);

    const handleValidate = async () => {
        if (onValidate) {
            try {
                setIsValidating(true);
                await onValidate(rows);
            } catch (error) {
                console.error('Validation error:', error);
                setErrorMessage(error.message || 'An unexpected error occurred. Please try again.');
                setShowErrorModal(true);
            } finally {
                setIsValidating(false);
            }
        }
    };

    const handleCloseErrorModal = () => {
        setShowErrorModal(false);
        setErrorMessage('');
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3.5 }}>
                <Typography variant="fw500s20c000000" sx={{ lineHeight: '20px' }}>
                    {selectedResourceType?.label || 'Resource Onboarding'}
                </Typography>
            </Box>

            {/* Data Preview Section */}
            <Grid2 container flexDirection={'column'} gap={1} sx={{ mb: 2.5 }}>
                <Typography variant="fw500s16c000000" sx={{ lineHeight: '16px' }}>
                    Data Preview
                </Typography>
                <Typography variant="fw500s14c000000" sx={{ lineHeight: '14px', color: '#686868' }}>
                    Showing first 5 rows from your CSV. Proceed view data validation and error report
                </Typography>
            </Grid2>

            {/* Data Table */}
            {rows.length > 0 ? (
                <Box sx={{ mb: 3, width: "calc(100vw - 169px)" }}>
                    <GenericDraggableTableView
			            tableID='DataUploadPreviewList'
                        tableColumns={tableColumns}
                        tableData={tableData}
                        initialColumns={tableColumns}
                        showPrimaryButton={false}
                        disableDownloads={true}
                        disablePagination={true}
                        disableSearch={true}
                        disableFiltersSettings={true}
                        disableColumnSettings={true}
                        isLoading={false}
                        numberOfFixedColumns={1}
                        disableTableHeader={true}
                    />
                </Box>
            ) : (
                <Box sx={{ p: 3, textAlign: 'center', border: '1px solid #E0E0E0', borderRadius: '12px', mb: 3 }}>
                    <Typography variant="body1" color="textSecondary">
                        No preview data available
                    </Typography>
                </Box>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <PrimaryFormLoadingActionButton
                    disabled={isValidating}
                    onClick={handleValidate}
                    loading={isValidating}
                    sx={{ borderRadius: '10px', p: 2 }}
                >
                    Validate Data
                </PrimaryFormLoadingActionButton>
            </Box>

            {/* Error Modal */}
            <ErrorModal
                open={showErrorModal}
                onClose={handleCloseErrorModal}
                errorMessage={errorMessage}
                buttonText="Go Back to Uploads"
                onButtonClick={onBackToTable}
            />
        </Box>
    );
};

export default Step3DataPreview;
